import React, { memo, useMemo } from 'react';
import { Card, Col, Placeholder, Row, Table } from 'react-bootstrap';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { FeedbackStats } from '../../../types';
import './AdminFeedbackStats.css';

interface AdminFeedbackStatsProps {
  stats: FeedbackStats | null;
  loading: boolean;
}

const RATING_COLORS: Record<number, string> = {
  1: '#dc3545',
  2: '#f17b3a',
  3: '#f5b301',
  4: '#7dd856',
  5: '#28a745',
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

const formatRating = (value: number): string => {
  if (!Number.isFinite(value)) return '0,0';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  }).format(value);
};

const computePercent = (count: number, total: number): string => {
  if (!total) return '0%';
  return `${Math.round((count * 100) / total)}%`;
};

const AdminFeedbackStats: React.FC<AdminFeedbackStatsProps> = memo(({ stats, loading }) => {
  const distributionData = useMemo(() => {
    if (!stats) return [];
    const dist = stats.distribution;
    return [
      { rating: 1, label: '1 estrela', count: dist.rating_1 },
      { rating: 2, label: '2 estrelas', count: dist.rating_2 },
      { rating: 3, label: '3 estrelas', count: dist.rating_3 },
      { rating: 4, label: '4 estrelas', count: dist.rating_4 },
      { rating: 5, label: '5 estrelas', count: dist.rating_5 },
    ];
  }, [stats]);

  if (loading && !stats) {
    return (
      <Row className="g-3 admin-feedback-stats">
        {[1, 2, 3].map((i) => (
          <Col key={i} xs={12} md={4}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <Placeholder as={Card.Title} animation="wave"><Placeholder xs={6} /></Placeholder>
                <Placeholder as="p" animation="wave"><Placeholder xs={8} /></Placeholder>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  if (!stats) return null;

  const total = stats.total;
  const average = stats.average_rating;
  const positives = stats.distribution.rating_4 + stats.distribution.rating_5;
  const positivesPct = total > 0 ? Math.round((positives / total) * 100) : 0;

  return (
    <Row className="g-3 admin-feedback-stats">
      <Col xs={12} md={4}>
        <Card className="h-100 border-0 shadow-sm admin-feedback-stats__card">
          <Card.Body>
            <div className="admin-feedback-stats__card-icon" aria-hidden="true">
              <i className="bi bi-chat-square-text-fill"></i>
            </div>
            <div className="admin-feedback-stats__card-label">Total de feedbacks</div>
            <div className="admin-feedback-stats__card-value">
              {formatNumber(total)}
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col xs={12} md={4}>
        <Card className="h-100 border-0 shadow-sm admin-feedback-stats__card">
          <Card.Body>
            <div className="admin-feedback-stats__card-icon" aria-hidden="true">
              <i className="bi bi-star-fill"></i>
            </div>
            <div className="admin-feedback-stats__card-label">Média geral</div>
            <div className="admin-feedback-stats__card-value">
              {formatRating(average)}
              <span className="admin-feedback-stats__card-suffix">/ 5</span>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col xs={12} md={4}>
        <Card className="h-100 border-0 shadow-sm admin-feedback-stats__card">
          <Card.Body>
            <div className="admin-feedback-stats__card-icon" aria-hidden="true">
              <i className="bi bi-emoji-smile-fill"></i>
            </div>
            <div className="admin-feedback-stats__card-label">Avaliações positivas (4-5)</div>
            <div className="admin-feedback-stats__card-value">
              {positivesPct}%
              <span className="admin-feedback-stats__card-suffix">
                ({formatNumber(positives)})
              </span>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col xs={12} lg={7}>
        <Card className="h-100 border-0 shadow-sm">
          <Card.Body>
            <h6 className="admin-feedback-stats__section-title">
              <i className="bi bi-bar-chart-fill me-2" aria-hidden="true"></i>
              Distribuição de notas
            </h6>
            {total === 0 ? (
              <p className="text-muted small mb-0">
                Sem dados no período selecionado.
              </p>
            ) : (
              <div className="admin-feedback-stats__chart-wrapper" role="img" aria-label="Gráfico de distribuição de notas">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={distributionData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(v) => [formatNumber(typeof v === 'number' ? v : 0), 'Total']}
                      labelStyle={{ color: '#1e305a', fontWeight: 600 }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {distributionData.map((entry) => (
                        <Cell key={entry.rating} fill={RATING_COLORS[entry.rating]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <ul className="admin-feedback-stats__legend" aria-hidden="true">
                  {distributionData.map((d) => (
                    <li key={d.rating}>
                      <span
                        className="admin-feedback-stats__legend-dot"
                        style={{ backgroundColor: RATING_COLORS[d.rating] }}
                      />
                      {d.label}: <strong>{formatNumber(d.count)}</strong>
                      <span className="text-muted ms-1">({computePercent(d.count, total)})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col xs={12} lg={5}>
        <Card className="h-100 border-0 shadow-sm">
          <Card.Body>
            <h6 className="admin-feedback-stats__section-title">
              <i className="bi bi-trophy-fill me-2" aria-hidden="true"></i>
              Itens mais avaliados
            </h6>
            {stats.top_workflows.length === 0 ? (
              <p className="text-muted small mb-0">
                Sem dados de escopos no período.
              </p>
            ) : (
              <div className="table-responsive">
                <Table size="sm" hover className="admin-feedback-stats__table mb-0">
                  <thead>
                    <tr>
                      <th>Escopo</th>
                      <th className="text-end">Avaliações</th>
                      <th className="text-end">Média</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_workflows.map((wf) => (
                      <tr key={wf.workflow_id ?? 'general'}>
                        <td>{wf.workflow_name ?? 'Plataforma (geral)'}</td>
                        <td className="text-end">{formatNumber(wf.total)}</td>
                        <td className="text-end">
                          <span className="admin-feedback-stats__avg">
                            {formatRating(wf.average_rating)}
                            <i
                              className="bi bi-star-fill ms-1 text-warning"
                              aria-hidden="true"
                            ></i>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
});

AdminFeedbackStats.displayName = 'AdminFeedbackStats';

export default AdminFeedbackStats;
